import { ImportCsvModal } from '@ovComponents/2-component/importCsvModal/importCsvModal';
import { Box, IconButton, Tooltip } from 'hephaestus/web';
import { UploadFileRounded as UploadFileRoundedIcon } from 'hephaestus/webIcons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../hooks/useLocalization';
import { AccountAllocationCsvReviewDialog } from './AccountAllocationCsvReviewDialog';
import { downloadAccountAllocationCsvTemplate } from './downloadCsvTemplate';
import {
  type AccountAllocationCsvUploadProp,
  useAccountAllocationCsvUpload,
} from './useAccountAllocationCsvUpload';

const NS = 'components:accountAllocationGrid.csvUpload';

export type AccountAllocationCsvControlsProp =
  AccountAllocationCsvUploadProp & {
    // Held while the grid hands over to Continue, so an upload can't change
    // what's being staged.
    uploadDisabled?: boolean;
  };

// The same entry point as Edit Allocations: an upload icon opens the shared
// import dialog, which also offers the template.
export const AccountAllocationCsvControls = ({
  uploadDisabled = false,
  ...props
}: AccountAllocationCsvControlsProp) => {
  const { t } = useTranslation();
  const { translateBackendString } = useLocalization();
  const [importOpen, setImportOpen] = useState(false);
  const { review, uploadFile, importReviewed, dismissReview } =
    useAccountAllocationCsvUpload(props);

  return (
    // The auto margin keeps this beside Undo in the space-between toolbar.
    <Box sx={{ ml: 'auto' }}>
      <Tooltip title={t(`${NS}.upload`)}>
        <span>
          <IconButton
            aria-label={t(`${NS}.upload`)}
            disabled={uploadDisabled}
            onClick={() => setImportOpen(true)}
          >
            <UploadFileRoundedIcon fontSize='inherit' />
          </IconButton>
        </span>
      </Tooltip>
      <ImportCsvModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={uploadFile}
        onDownloadTemplate={() =>
          downloadAccountAllocationCsvTemplate(
            props.report,
            t('components:accountAllocationGrid.csvTemplate.filename'),
            { t, translateBackendString },
          )
        }
        // The upload reports its own outcome: a count, nothing changed, or
        // the issues to review.
        showSuccessToast={false}
        title={t(`${NS}.modalTitle`)}
        description={t(`${NS}.modalDescription`)}
        importButtonLabel={t(`${NS}.import`)}
        testIdPrefix='account-allocation'
      />
      <AccountAllocationCsvReviewDialog
        review={review}
        onImport={importReviewed}
        onDismiss={dismissReview}
      />
    </Box>
  );
};
